import Vue from 'vue'
import { $axAuthState } from './$axAuthStateManager'
import { $axFrontendHelper } from './$axFrontendHelper'
import { AxFavoritItem, itemFields } from '@/types/AxItem'
import { $toast } from '../services/$toast'
import { $Redirecter } from '@/services/redirecter'
import { cecPaths } from '@/cecSitePath'

type ItemId = string

export type AxItemFavoriteResponse = {
  labels: string[]
  item: AxFavoritItem
}[]

const favLabel = 'favorites'

export class ItemFavoriteManager {
  favvedItemDataByItemId: { [itemId: string]: AxFavoritItem | true }
  /**
   * お気に入りボタンを多数設置するときに、常にフェッチしてたらアホなので、
   * プロセスでは1度のみfetchするようにする
   */
  fetchAllFavPromisePlaceholder: null | Promise<any>
  constructor() {
    Vue.set(this, 'favvedItemDataByItemId', {})
    this.fetchAllFavPromisePlaceholder = null
  }

  /**
   * お気に入り商品を取得して、 favvedItemDataByItemId を更新
   * 実装上、ラベルは "favorites" で固定している
   */
  async refetch(): Promise<void> {
    // ログイン状態チェック
    if (!$axAuthState.isLoggedIn) {
      $toast.error('ログインを行ってから再度やり直してください。')
      // 自動でリダイレクトする場合はここで定義
      $Redirecter.openSite(cecPaths.accounts.login, false)
    }
    if (this.fetchAllFavPromisePlaceholder === null) {
      this.fetchAllFavPromisePlaceholder = new Promise(async (resolve) => {
        const favved = await $axFrontendHelper.request<{ favorites: AxItemFavoriteResponse }>({
          path: '/favorite/items/get',
          data: {
            query: {
              labels: [favLabel],
              operator: 'and',
            },
            request: {
              fields: itemFields,
            },
          },
        })
        const byItemId = favved.result.favorites.reduce((res, fav) => {
          res[fav.item.skus ? fav.item.skus.id : fav.item.id] = fav.item
          return res
        }, {})
        Vue.set(this, 'favvedItemDataByItemId', byItemId)
        /**
         * 再取得は10秒以降なら許可する
         */
        setTimeout(() => {
          this.fetchAllFavPromisePlaceholder = null
        }, 10000)
        resolve(favved)
      })
    }
    return this.fetchAllFavPromisePlaceholder
  }

  async toggle(itemId: ItemId) {
    const isFavved = this.favvedItemDataByItemId[itemId]
    isFavved ? await this.remove(itemId) : await this.add(itemId)
  }

  async add(itemId: ItemId) {
    if (!$axAuthState.isLoggedIn) {
      $toast.warning('お気に入り登録にはログインが必要です。')
      return
    }
    try {
      await $axFrontendHelper.request<{ favorites: AxFavoritItem[] }>({
        path: '/favorite/items/put',
        data: {
          query: {
            'item.skus.id': itemId,
            labels: [favLabel],
            orverwirte_label: false,
          },
        },
      })
      Vue.set(this.favvedItemDataByItemId, itemId, true)
      $toast.success('お気に入りに追加しました。')
    } catch (e) {
      $toast.error('お気に入りに追加出来ませんでした。後ほど再度お試しください。')
    }
  }

  async remove(itemId: ItemId) {
    if (!$axAuthState.isLoggedIn) {
      $toast.warning('お気に入りから削除するにはログインが必要です。')
      return
    }
    try {
      await $axFrontendHelper.request<{ favorites: AxFavoritItem[] }>({
        path: '/favorite/items/delete',
        data: {
          query: {
            'item.skus.ids': [itemId],
            labels: [favLabel],
          },
        },
      })
      Vue.delete(this.favvedItemDataByItemId, itemId)
      $toast.success('お気に入りから削除しました。')
    } catch (e) {
      $toast.success('お気に入りから削除出来ませんでした。後ほど再度お試しください。')
    }
  }
}

export const $itemFavoriteManager = Vue.observable(new ItemFavoriteManager())
